import { DataStore } from 'common';

export const PACKAGE_ASSIGNMENTS_KEY = 'seatingPackageSeatAssignments';

export function getPackageTicketItems(packageData) {
  if (!packageData?.items) return [];

  return packageData.items
    .filter(item => item.item_type === 'event_price_id')
    .map(item => {
      const ticketTypeId = item.item_id ?? item.item_details?.id;
      if (ticketTypeId == null || ticketTypeId === '') return null;

      return {
        packageItemId: String(item.id ?? item.item_id),
        ticketTypeId: Number(ticketTypeId),
        title: item.item_details?.title || 'Ticket',
      };
    })
    .filter(Boolean);
}

export function getPackageProductItems(packageData) {
  if (!packageData?.items) return [];

  return packageData.items.filter(item =>
    item.item_type === 'product_id' &&
    item.item_details?.variants?.length > 0
  );
}

export function packageItemId(item, index) {
  return String(item.id || item.item_id || `item-${index}`);
}

export function buildPackageSlots(selectedPackages) {
  const slots = [];

  Object.values(selectedPackages || {}).forEach(pkg => {
    const count = pkg.count || 0;
    const ticketItems = pkg.ticketItems || [];

    for (let copyIndex = 1; copyIndex <= count; copyIndex++) {
      ticketItems.forEach(item => {
        slots.push({
          slotId: `${pkg.packageId}:${copyIndex}:${item.packageItemId}`,
          packageId: pkg.packageId,
          packageName: pkg.name,
          copyIndex,
          packageItemId: item.packageItemId,
          ticketTypeId: Number(item.ticketTypeId),
          ticketTypeTitle: item.title,
        });
      });
    }
  });

  return slots;
}

export function slotsAboveCount(pkg, count) {
  if (!pkg) return [];

  return buildPackageSlots({
    [pkg.packageId]: {
      ...pkg,
      count: pkg.count || 0,
    },
  }).filter(slot => slot.copyIndex > count);
}

export function variantsStateFromPackages(selectedPackages) {
  const variants = {};

  Object.values(selectedPackages || {}).forEach(pkg => {
    Object.entries(pkg.variants || {}).forEach(([itemId, byIndex]) => {
      Object.entries(byIndex || {}).forEach(([index, info]) => {
        variants[`${pkg.packageId}-${itemId}-${index}`] = info;
      });
    });
  });

  return variants;
}

export function filterVariantsForCount(existingVariants, count) {
  const filteredVariants = {};

  Object.keys(existingVariants || {}).forEach(itemId => {
    const itemVariants = existingVariants[itemId] || {};
    const filteredItemVariants = {};

    Object.keys(itemVariants).forEach(index => {
      if (parseInt(index, 10) <= count) {
        filteredItemVariants[index] = itemVariants[index];
      }
    });

    if (Object.keys(filteredItemVariants).length > 0) {
      filteredVariants[itemId] = filteredItemVariants;
    }
  });

  return filteredVariants;
}

export function arePackageVariantsSelected(eventPackages, selectedPackages, packageVariants) {
  for (const pkg of Object.values(selectedPackages || {})) {
    if (!pkg.count) continue;

    const source = (eventPackages || []).find(item => String(item.id) === String(pkg.packageId));
    const productItems = getPackageProductItems(source);

    for (let itemIndex = 0; itemIndex < productItems.length; itemIndex++) {
      const item = productItems[itemIndex];
      const itemId = packageItemId(item, itemIndex);

      for (let copyIndex = 1; copyIndex <= pkg.count; copyIndex++) {
        if (!packageVariants[`${pkg.packageId}-${itemId}-${copyIndex}`]) {
          return false;
        }
      }
    }
  }

  return true;
}

export function writeSelectedPackages(selectedPackages) {
  const existing = DataStore.get('selectedTickets') || {
    selectedTickets: {},
    selectedMerch: {},
    selectedPackages: {},
    price: 0,
    tickets: 0,
  };

  const sum = (items, field) => Object.values(items || {}).reduce((total, item) => total + (item[field] || 0), 0);
  const packagePrice = sum(selectedPackages, 'totalPrice');
  const packageCount = sum(selectedPackages, 'count');

  DataStore.set('selectedTickets', {
    ...existing,
    selectedPackages,
    price: sum(existing.selectedTickets, 'totalPrice') + sum(existing.selectedMerch, 'totalPrice') + packagePrice,
    tickets: sum(existing.selectedTickets, 'count') + sum(existing.selectedMerch, 'count') + packageCount,
  });
}

export function loadPackageAssignments() {
  return DataStore.get(PACKAGE_ASSIGNMENTS_KEY) || {};
}

export function savePackageAssignments(assignments) {
  DataStore.set(PACKAGE_ASSIGNMENTS_KEY, assignments || {});
}

export function findSeatKey(seatsMap, seatKey) {
  if (!seatsMap || seatKey == null) return null;
  if (seatsMap.has(seatKey)) return seatKey;

  const asNumber = Number(seatKey);
  if (!Number.isNaN(asNumber) && seatsMap.has(asNumber)) return asNumber;

  const asString = String(seatKey);
  if (seatsMap.has(asString)) return asString;

  return null;
}

export function findMatchingGeneralAdmissionKey(seatsMap, assignment) {
  for (const [key, seat] of seatsMap.entries()) {
    if (!seat?.isGeneralAdmission || seat.slotId) continue;
    if (Number(seat.ticketTypeId) !== Number(assignment.ticketTypeId)) continue;
    if (String(seat.blockId) !== String(assignment.blockId)) continue;
    if ((seat.quantity || 1) <= 0) continue;
    return key;
  }

  return null;
}

export function applyAssignmentsToSeats(seatsMap, assignments) {
  const next = new Map(seatsMap);
  const kept = {};

  Object.entries(assignments || {}).forEach(([slotId, assignment]) => {
    if (!assignment) return;

    if (assignment.isGeneralAdmission) {
      const gaKey = findSeatKey(next, assignment.seatKey) || findMatchingGeneralAdmissionKey(next, assignment);
      if (gaKey == null) return;

      const seat = next.get(gaKey);
      if (!seat?.isGeneralAdmission) return;
      if (Number(seat.ticketTypeId) !== Number(assignment.ticketTypeId)) return;

      const packageKey = `ga_pkg_${slotId}`;
      const packageSeat = {
        ...seat,
        slotId,
        packageId: assignment.packageId,
        packageIndex: assignment.packageIndex,
        packageItemId: assignment.packageItemId,
        packageName: assignment.packageName,
        quantity: 1,
        isGeneralAdmission: true,
      };

      if (String(gaKey) === String(packageKey)) {
        next.set(gaKey, packageSeat);
      } else if ((seat.quantity || 1) <= 1) {
        next.delete(gaKey);
        next.set(packageKey, packageSeat);
      } else {
        next.set(gaKey, {
          ...seat,
          quantity: (seat.quantity || 1) - 1,
        });
        next.set(packageKey, packageSeat);
      }

      kept[slotId] = {
        ...assignment,
        seatKey: packageKey,
        isGeneralAdmission: true,
      };
      return;
    }

    if (!assignment.seatKey) return;

    const seatKey = findSeatKey(next, assignment.seatKey);
    if (seatKey == null) return;

    const seat = next.get(seatKey);
    if (Number(seat.ticketTypeId) !== Number(assignment.ticketTypeId)) return;

    next.set(seatKey, {
      ...seat,
      slotId,
      packageId: assignment.packageId,
      packageIndex: assignment.packageIndex,
      packageItemId: assignment.packageItemId,
      packageName: assignment.packageName,
    });
    kept[slotId] = {
      ...assignment,
      seatKey,
    };
  });

  return { seatsMap: next, assignments: kept };
}

export function buildPackagesCheckoutPayload(selectedPackages, seatsMap) {
  return Object.values(selectedPackages || {})
    .filter(pkg => pkg.count > 0)
    .map(pkg => {
      const variants = [];

      Object.entries(pkg.variants || {}).forEach(([itemId, byIndex]) => {
        Object.values(byIndex || {}).forEach(variant => {
          variants.push({
            ...variant,
            package_item_id: parseInt(itemId, 10),
          });
        });
      });

      const seats = [];

      Array.from(seatsMap?.entries?.() || []).forEach(([seatKey, seatData]) => {
        if (!seatData?.slotId || String(seatData.packageId) !== String(pkg.packageId)) return;

        seats.push({
          package_item_id: parseInt(seatData.packageItemId, 10),
          package_index: seatData.packageIndex,
          ticket_type_id: seatData.ticketTypeId,
          seat_id: seatData.isGeneralAdmission ? undefined : seatKey,
          block_id: seatData.blockId,
          is_general_admission: !!seatData.isGeneralAdmission,
        });
      });

      return {
        package_id: pkg.packageId,
        quantity: pkg.count,
        variants,
        seats,
      };
    });
}

export function packageSeatKey(seat) {
  if (seat?.isGeneralAdmission && seat?.slotId) {
    return `ga_pkg_${seat.slotId}`;
  }

  return seat?.seatId;
}
